import { Box, ButtonBase, Grow, Popper, Stack } from "@mui/material";
import { alpha, styled, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useLoginStatus } from "../../../context/LoginStatusContext";
import { useCustomNavigate } from "../../../hooks/useCustomNavigate";
import { Images } from "../../../assets";
import CenterContentsManager from "./CenterContentsManager";

// スタイル付きのタブコンテナ
const TabContainer = styled(Stack)(({ theme }) => ({
  backdropFilter: "blur(8px)",
  padding: theme.spacing(0.5),
  borderRadius: 18,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  height: 36,
  gap: theme.spacing(0.25),
  color: theme.palette.text.primary,
}));

// メニューペーパー
const MenuPaper = styled(Box)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: "blur(8px)",
  color: theme.palette.text.primary,
  padding: theme.spacing(1),
  maxWidth: 480,
  width: 480,
  boxShadow: theme.shadows[2],
  overflow: "hidden",
}));

// 型定義
interface ButtonConfig {
  path: string;
  label: string;
  img: React.ReactElement;
}

const CenterContents: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useCustomNavigate();
  const location = useLocation();
  const { isLoggedIn } = useLoginStatus();
  const [activeContent, setActiveContent] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popperVisible, setPopperVisible] = useState(false);
  const [popperKey, setPopperKey] = useState(0); // ポッパーの再レンダリングを強制するためのキー
  const containerRef = useRef<HTMLDivElement>(null);
  const hoverTimer = useRef<NodeJS.Timeout | null>(null);
  const leaveTimer = useRef<NodeJS.Timeout | null>(null);
  const pointerMovedToMenuRef = useRef(false); // マウスがメニューに移動したかのフラグ
  const isMouseInsideTab = useRef(false); // マウスがタブ内にあるか
  const isMouseInsidePopper = useRef(false); // マウスがポッパー内にあるか

  // 現在のパスからパスの一部を取得するヘルパー関数
  const getPathPart = useCallback((index: number) => location.pathname.split("/")[index] || "", [location.pathname]);

  // ウィンドウフォーカスの監視とタイマーのクリーンアップ
  useEffect(() => {
    // ウィンドウがフォーカスを失った時のハンドラー
    const handleWindowBlur = () => {
      // ポッパーの状態をリセット
      setPopperVisible(false);
      setAnchorEl(null);
      isMouseInsideTab.current = false;
      isMouseInsidePopper.current = false;
      pointerMovedToMenuRef.current = false;
    };

    // ウィンドウがフォーカスを得た時のハンドラー
    const handleWindowFocus = () => {
      // 状態をクリーンな状態に
      if (popperVisible) {
        setPopperVisible(false);
        setAnchorEl(null);
        isMouseInsideTab.current = false;
        isMouseInsidePopper.current = false;
        pointerMovedToMenuRef.current = false;
      }
    };

    // イベントリスナーを追加
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("focus", handleWindowFocus);

    return () => {
      // イベントリスナーとタイマーをクリーンアップ
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("focus", handleWindowFocus);
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
    };
  }, [popperVisible]);

  // 状態を完全にリセットする関数
  const resetAllStates = useCallback(() => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }

    setPopperVisible(false);
    setAnchorEl(null);
    isMouseInsideTab.current = false;
    isMouseInsidePopper.current = false;
    pointerMovedToMenuRef.current = false;
  }, []);

  // ポッパーを閉じる関数（アニメーション付き）
  const closePopperWithAnimation = useCallback(() => {
    // ポッパー内にマウスがある場合は閉じない
    if (isMouseInsidePopper.current) return;
    // タブ内にマウスがある場合は閉じない
    if (isMouseInsideTab.current) return;

    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }

    setPopperVisible(false);

    // ポッパーのアニメーション終了後に状態をクリア
    setTimeout(() => {
      if (!isMouseInsideTab.current && !isMouseInsidePopper.current) {
        setAnchorEl(null);
        pointerMovedToMenuRef.current = false;
      }
    }, 300); // Growアニメーションが終わった後
  }, []);

  // ボタン設定
  const buttons: ButtonConfig[] = [
    {
      path: "",
      label: "title",
      img: <img src={Images.tenak.small} style={{ width: 18, height: 18, marginLeft: -4 }} alt="Tenak.ai" />,
    },
    {
      path: "juno",
      label: "juno.title",
      img: <img src={Images.juno.mini2} style={{ width: 18, height: 18, marginLeft: -4 }} alt="STUDIO JUNO" />,
    },
    {
      path: "notes-nebula",
      label: "notesNebula.title",
      img: <img src={Images.notesNebula.png} style={{ width: 18, height: 18, marginLeft: -4 }} alt="Notes Nebula" />,
    },
  ];

  // ホバーの開始時処理
  const handleButtonMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLElement>, tabName: string) => {
      if (!isLoggedIn) return;

      // マウスがタブ内にあることをマーク
      isMouseInsideTab.current = true;

      // タイマーをクリア
      if (leaveTimer.current) {
        clearTimeout(leaveTimer.current);
        leaveTimer.current = null;
      }

      // 遅延を設定してホバー状態を設定
      if (hoverTimer.current) clearTimeout(hoverTimer.current);

      // 特別にTenakAIの場合は空文字列を設定
      const menuName = tabName === "" ? "" : tabName;

      // 新しいタブへのホバー時に、表示内容を切り替える
      if (activeContent !== menuName) {
        if (popperVisible) {
          // すでにポッパーが表示されている場合は内容だけ切り替え
          setActiveContent(menuName);
          setAnchorEl(event.currentTarget);
          setPopperKey((prevKey) => prevKey + 1); // キーを更新して強制再レンダリング
        } else {
          // ポッパーが表示されていない場合は新規表示
          setActiveContent(menuName);
          setAnchorEl(event.currentTarget);
          setPopperKey((prevKey) => prevKey + 1);
          setPopperVisible(true);
        }
      } else if (!popperVisible) {
        // 同じタブだがポッパーが表示されていない場合は表示
        setActiveContent(menuName);
        setAnchorEl(event.currentTarget);
        setPopperKey((prevKey) => prevKey + 1);
        setPopperVisible(true);
      }
    },
    [isLoggedIn, activeContent, popperVisible]
  );

  // ボタンからマウスが離れた時の処理
  const handleButtonMouseLeave = useCallback(() => {
    // マウスがタブから出たこと
    isMouseInsideTab.current = false;

    // ポッパー内にマウスがある場合は何もしない
    if (isMouseInsidePopper.current) return;

    // 少し遅延させてからポッパーを閉じる（ポッパーに移動する時間を与える）
    leaveTimer.current = setTimeout(() => {
      // この時点でもポッパー内にマウスがない場合だけ閉じる
      if (!isMouseInsidePopper.current) {
        closePopperWithAnimation();
      }
      leaveTimer.current = null;
    }, 100);
  }, [closePopperWithAnimation]);

  // コンテナからマウスが離れた時の処理
  const handleContainerMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      // ポッパーへの移動中かどうかを判定
      const relatedTarget = e.relatedTarget;
      // relatedTargetがHTMLElementであり、closestメソッドを持っている場合のみ実行
      if (relatedTarget && relatedTarget instanceof HTMLElement && relatedTarget.closest('[role="tooltip"]')) {
        // ポッパーにマウスが移動した場合は何もしない
        return;
      }

      handleButtonMouseLeave();
    },
    [handleButtonMouseLeave]
  );

  // ポッパーにマウスが入った時
  const handlePopperMouseEnter = useCallback(() => {
    isMouseInsidePopper.current = true;
    pointerMovedToMenuRef.current = true;

    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
  }, []);

  // ポッパーからマウスが出た時
  const handlePopperMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      isMouseInsidePopper.current = false;

      // タブへの移動中かどうかを判定
      const relatedTarget = e.relatedTarget;
      // relatedTargetがHTMLElementであることを確認
      if (
        relatedTarget &&
        relatedTarget instanceof HTMLElement &&
        containerRef.current &&
        containerRef.current.contains(relatedTarget)
      ) {
        // タブにマウスが移動した場合は何もしない
        return;
      }

      // 少し遅延させてからポッパーを閉じる
      leaveTimer.current = setTimeout(() => {
        // この時点でもタブ内にマウスがない場合だけ閉じる
        if (!isMouseInsideTab.current) {
          closePopperWithAnimation();
        }
        leaveTimer.current = null;
      }, 100);
    },
    [closePopperWithAnimation]
  );

  // ページ遷移処理
  const handleNavigate = useCallback(
    (path: string) => {
      // ポッパーを閉じてから遷移
      resetAllStates();
      navigate(path === "" ? "/" : `/${path}`);
    },
    [navigate, resetAllStates]
  );

  // ボタンのアクティブ状態を判定
  const isActiveButton = useCallback(
    (path: string) => {
      const currentPath = getPathPart(2);
      return path === "" ? !["juno", "notes-nebula"].includes(currentPath) : path === currentPath;
    },
    [getPathPart]
  );

  // articles パスで非ログインユーザーなら非表示
  if (getPathPart(3) === "articles" && !isLoggedIn) return null;

  return (
    <Box
      ref={containerRef}
      onMouseLeave={handleContainerMouseLeave}
      sx={{ display: "inline-flex", position: "relative" }}
    >
      <TabContainer direction="row">
        {buttons.map((button, index) => {
          const isActive = isActiveButton(button.path);

          return (
            <ButtonBase
              key={index}
              onClick={() => {
                handleNavigate(button.path);
              }}
              onMouseEnter={(e) => handleButtonMouseEnter(e, button.path)}
              onMouseLeave={handleButtonMouseLeave}
              sx={{
                color: isActive ? "primary.light" : theme.palette.text.primary,
                backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.2) : "transparent",
                borderRadius: 3.5,
                width: 160,
                height: 26,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
                transition: "background-color 0.3s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                padding: "4px 8px",
              }}
            >
              {button.img}
              <Typography fontWeight={isActive ? "bold" : "normal"} variant="caption">
                {t(button.label)}
              </Typography>
            </ButtonBase>
          );
        })}
      </TabContainer>

      <Popper
        key={popperKey}
        open={Boolean(isLoggedIn) && Boolean(anchorEl) && popperVisible}
        anchorEl={anchorEl}
        placement="bottom"
        sx={{ pt: 1 }}
        style={{ zIndex: 1400 }}
        onMouseEnter={handlePopperMouseEnter}
        onMouseLeave={handlePopperMouseLeave}
        modifiers={[
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              altAxis: true,
              altBoundary: true,
              boundary: "viewport",
            },
          },
        ]}
        transition
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} timeout={300} style={{ transformOrigin: "center top" }}>
            <MenuPaper>
              <CenterContentsManager activeMenu={activeContent} />
            </MenuPaper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

export default React.memo(CenterContents);
